//--------------------------------------------------------------------//
//******************** agentSubscriptionsTab *************************//
//--------------------------------------------------------------------//
// Lists the agent's event subscriptions in a table with an active
// toggle and a popup to create/edit subscription details (app_id,
// event_type, prompt, filter, active_subscription).
// gh-filter is embedded inline in the dialog to configure filter
// conditions. filterDataModel.recipient is mutated in place (not
// replaced) when app_id changes so gh-filter's internal watch fires.
import agentSubscriptionsTemplateUrl from './agent-subscriptions.template.html';
import agentSubscriptionsDialogTemplateUrl from './agent-subscriptions.dialog.html';

angular.module('agentsTableModule')

.directive('agentSubscriptionsTab', ['GhDialog', 'AgentSubscriptionsService', '$templateCache', function (GhDialog, AgentSubscriptionsService, $templateCache) {
    return {
        restrict: 'E',
        scope: {
            agent: '='
        },
        controller: ['$scope', 'localizationService', function ($scope, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            $scope.eventTypeOptions = [
                { name: 'Add Item', value: 'gh_item_add' },
                { name: 'Update Item', value: 'gh_item_update' }
            ];

            $scope.getEventTypeName = function (value) {
                const opt = $scope.eventTypeOptions.find((o) => o.value === value);
                return opt ? opt.name : value;
            };

            $scope.subscriptions = [];

            const agentId = $scope.agent._id;

            let appNameMap = {};

            async function loadSubscriptions() {
                try {
                    const [result, appsList] = await Promise.all([
                        AgentSubscriptionsService.getSubscriptions(agentId),
                        gudhub.getAppsList()
                    ]);
                    appNameMap = (appsList || []).reduce((map, app) => {
                        map[app.app_id] = app.app_name;
                        return map;
                    }, {});
                    const subs = (result && result.subscriptions) || [];
                    subs.forEach((sub) => { sub.appName = appNameMap[sub.app_id] || sub.app_id; });
                    $scope.subscriptions = subs;
                } catch (err) {
                    console.error('Failed to load subscriptions:', err);
                    $scope.subscriptions = [];
                }
                $scope.$applyAsync();
            }
            loadSubscriptions();

            function showErrorPopup(message) {
                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="delete_agent-popup">
                                <span style="position:absolute;top:12px;right:12px;cursor:pointer;" ng-click="cancel()" gh-icon="cross 0D99FF 30px"></span>
                                <div class="dialog-share-group">
                                    <span gh-icon="alert FF0D0D 65px"></span>
                                    <p class="dialog-share-group__title">{{translate('Error')}}</p>
                                </div>
                                <div class="dialog-share-group__content">
                                    <p class="info">{{errorMessage}}</p>
                                    <div class="dialog-share-group__content__buttons">
                                        <button class="dialog-share-group__content__buttons__cancel btn btn-blue" ng-click="cancel()">
                                            {{translate('OK')}}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        `
                    },
                    locals: { errorMessage: message },
                    controller: ['$scope', 'errorMessage', 'localizationService', function ($dialogScope, errorMessage, localizationService) {
                        const d = localizationService.getDictionary('account_settings');
                        $dialogScope.translate = (word) => localizationService.translate(word, d);
                        $dialogScope.errorMessage = errorMessage;
                    }]
                });
            }

            // gh-boolean's internal md-switch flips active_subscription via ng-model
            // before this bubbled click handler runs; stop the row click from opening
            // the dialog and best-effort sync the already-new value.
            $scope.toggleSubscription = function (e, sub) {
                e.stopPropagation();
                AgentSubscriptionsService.toggleSubscription(agentId, sub.id, sub.active_subscription)
                    .catch((err) => {
                        sub.active_subscription = sub.active_subscription ? 0 : 1;
                        $scope.$applyAsync();
                        const message = err.message.replace(/\b(\d+)\b/g, (id) => appNameMap[id] || id);
                        showErrorPopup(message);
                    });
            };

            function deleteSubscription(sub) {
                const index = $scope.subscriptions.findIndex((s) => s.id === sub.id);
                if (index !== -1) {
                    $scope.subscriptions.splice(index, 1);
                }
                AgentSubscriptionsService.deleteSubscription(agentId, sub.id)
                    .catch((err) => {
                        console.error('Failed to delete subscription:', err);
                    });
            }

            $scope.removeSubscription = function (e, sub) {
                e.stopPropagation();

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="delete_agent-popup">
                                <div class="dialog-share-group">
                                    <span gh-icon="rubbish FF0D0D 65px"></span>
                                    <p class="dialog-share-group__title">{{translate('Deleting Subscription!')}}</p>
                                </div>
                                <div class="dialog-share-group__content">
                                    <p class="info">
                                        {{translate('Do you really want to delete this subscription? You will not be able to restore it.')}}
                                    </p>
                                    <div class="dialog-share-group__content__buttons">
                                        <button class="dialog-share-group__content__buttons__add btn btn-blue-reverse" ng-click="confirmDelete()">
                                            {{translate('Delete')}}
                                        </button>
                                        <button class="dialog-share-group__content__buttons__cancel btn btn-blue" ng-click="cancel()">
                                            {{translate('Cancel')}}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        `
                    },
                    locals: { sub },
                    controller: ['$scope', 'sub', 'localizationService', function ($dialogScope, sub, localizationService) {
                        const d = localizationService.getDictionary('account_settings');
                        $dialogScope.translate = (word) => localizationService.translate(word, d);
                        $dialogScope.confirmDelete = function () {
                            deleteSubscription(sub);
                            $dialogScope.cancel();
                        };
                    }]
                });
            };

            $scope.addSubscription = function () {
                $scope.openSubscriptionDialog(null);
            };

            $scope.openSubscriptionDialog = function (sub) {
                const isNew = !sub;

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: $templateCache.get(agentSubscriptionsDialogTemplateUrl)
                    },
                    locals: {
                        sub,
                        isNew,
                        eventTypeOptions: $scope.eventTypeOptions
                    },
                    controller: ['$scope', 'sub', 'isNew', 'eventTypeOptions', 'localizationService',
                        function ($dialogScope, sub, isNew, eventTypeOptions, localizationService) {
                            const d = localizationService.getDictionary('account_settings');
                            $dialogScope.translate = (word) => localizationService.translate(word, d);
                            $dialogScope.isNew = isNew;
                            $dialogScope.eventTypeOptions = eventTypeOptions;

                            $dialogScope.form = {
                                subscription_name: sub ? sub.subscription_name : '',
                                app_id: sub ? sub.app_id : null,
                                event_type: sub ? sub.event_type : '',
                                prompt: sub ? sub.prompt : '',
                                filter: sub ? (sub.filter ? sub.filter.slice() : []) : [],
                                active_subscription: sub ? sub.active_subscription : 0,
                                voice_execution: sub ? sub.voice_execution : false,
                                field_id: sub ? sub.field_id : null
                            };

                            // gh-user-selecting with multiple_value stores selections as a
                            // comma-separated string in field_value. Pre-populate from the
                            // existing listen_for_users number array when editing.
                            $dialogScope.listenForUsersFieldModel = {
                                data_type: 'user',
                                data_model: {
                                    multiple_value: true,
                                    photo_size: 40,
                                    display_type: 'photo_full_name'
                                },
                                field_value: (sub && sub.listen_for_users && sub.listen_for_users.length)
                                    ? sub.listen_for_users.join(',')
                                    : ''
                            };

                            // filterDataModel must live on $dialogScope so gh-filter can bind to it.
                            // Mutate recipient in place (never replace the object) — gh-filter's
                            // internal watch is bound to the original recipient reference.
                            $dialogScope.filterDataModel = {
                                recipient: {
                                    app_id: $dialogScope.form.app_id,
                                    current_app: $dialogScope.form.app_id
                                }
                            };

                            $dialogScope.$watch('form.app_id', function (newVal, oldVal) {
                                if (newVal !== oldVal) {
                                    $dialogScope.filterDataModel.recipient.app_id = newVal;
                                    $dialogScope.filterDataModel.recipient.current_app = newVal;
                                }
                            });

                            $dialogScope.submitted = false;
                            $dialogScope.errors = {};

                            $dialogScope.validate = function () {
                                $dialogScope.errors = {
                                    app_id: !$dialogScope.form.app_id,
                                    event_type: !$dialogScope.form.event_type,
                                    prompt: !$dialogScope.form.prompt || !$dialogScope.form.prompt.trim(),
                                    field_id: $dialogScope.form.voice_execution && !$dialogScope.form.field_id
                                };
                                return !Object.values($dialogScope.errors).some(Boolean);
                            };

                            $dialogScope.saveSubscription = async function () {
                                $dialogScope.submitted = true;
                                if (!$dialogScope.validate()) return;

                                const rawUsers = $dialogScope.listenForUsersFieldModel.field_value;
                                const payload = JSON.parse(angular.toJson({
                                    subscription_name: $dialogScope.form.subscription_name || '',
                                    app_id: $dialogScope.form.app_id,
                                    event_type: $dialogScope.form.event_type,
                                    prompt: $dialogScope.form.prompt,
                                    filter: $dialogScope.form.filter || [],
                                    listen_for_users: rawUsers
                                        ? rawUsers.split(',').map(Number).filter(Boolean)
                                        : [],
                                    active_subscription: $dialogScope.form.active_subscription,
                                    voice_execution: $dialogScope.form.voice_execution,
                                    field_id: $dialogScope.form.voice_execution ? $dialogScope.form.field_id : null
                                }));

                                if (isNew) {
                                    const newSub = Object.assign({ id: Date.now(), appName: appNameMap[payload.app_id] || payload.app_id }, payload);
                                    $scope.subscriptions.push(newSub);
                                    try {
                                        // The Date.now() placeholder above is never accepted by the
                                        // server (unlike scheduling's task_id, subscription_id is
                                        // always server-generated) — swap in the real id so a toggle
                                        // right after creation doesn't 404 on the fake one.
                                        const result = await AgentSubscriptionsService.createSubscription(agentId, payload);
                                        newSub.id = result.subscription.subscription_id;
                                    } catch (err) {
                                        const idx = $scope.subscriptions.indexOf(newSub);
                                        if (idx !== -1) $scope.subscriptions.splice(idx, 1);
                                        $scope.$applyAsync();
                                        const message = err.message.replace(/\b(\d+)\b/g, (id) => appNameMap[id] || id);
                                        showErrorPopup(message);
                                        return;
                                    }
                                } else {
                                    Object.assign(sub, payload, { appName: appNameMap[payload.app_id] || payload.app_id });
                                    try {
                                        await AgentSubscriptionsService.updateSubscription(agentId, sub.id, payload);
                                    } catch (err) {
                                        const message = err.message.replace(/\b(\d+)\b/g, (id) => appNameMap[id] || id);
                                        showErrorPopup(message);
                                        return;
                                    }
                                }

                                $scope.$applyAsync();
                                $dialogScope.cancel();
                            };
                        }]
                });
            };
        }],
        templateUrl: agentSubscriptionsTemplateUrl
    };
}]);
